import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  CallReceived as ReceiptIcon,
  CallMade as IssueIcon,
  SwapHoriz as TransferIcon,
  DeleteForever as ScrapIcon,
  Tune as AdjustmentIcon,
  Undo as ReturnIcon,
  WorkOutline as WorkOrderIcon,
  Inventory2 as LotIcon,
} from '@mui/icons-material';

// ─── Constants ────────────────────────────────────────────────────────────────

const API_BASE =
  ((process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1')
    .replace(/\/api\/v\d+$/, '') + '/api/v1');

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface MaterialDefinition {
  name?: string;
  itemCode?: string;
}

interface Lot {
  id: string;
  lotNumber: string;
  batchNumber?: string;
  materialDefinitionId: string;
  materialDefinition?: MaterialDefinition;
  quantityValue: number;
  quantityUom: string;
  productionDate?: string;
  expiryDate?: string;
  supplierName?: string;
  supplierLotNumber?: string;
  status: string;
}

interface WorkOrderRef {
  id: string;
  number: string;
  product: string;
  status: string;
}

interface Transaction {
  id?: string;
  type: string;
  timestamp: string;
  quantity: number;
  uom: string;
  from?: string;
  to?: string;
  reason?: string;
  performedBy?: string;
  workOrder?: WorkOrderRef;
}

interface TraceData {
  lot: Lot;
  transactions: Transaction[];
  usedInWorkOrders: WorkOrderRef[];
}

interface TxMetaEntry {
  icon: React.ReactElement;
  color: string;
  label: string;
}

interface LotDetailCardProps {
  lot: Lot;
}

interface TransactionTimelineProps {
  transactions: Transaction[];
}

interface UsedInWorkOrdersProps {
  workOrders: WorkOrderRef[];
}

export interface LotGenealogyProps {
  initialLotNumber?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function apiFetch(path: string): Promise<unknown> {
  const token = localStorage.getItem('token');
  const resp = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!resp.ok) throw { message: `HTTP ${resp.status} — ${resp.statusText}` };
  return resp.json();
}

function formatDate(ts: string | undefined): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function formatDateOnly(d: string | undefined): string {
  if (!d) return '—';
  return d.slice(0, 10);
}

// ─── Transaction type icon + colour ──────────────────────────────────────────

const TX_META: Record<string, TxMetaEntry> = {
  receipt:    { icon: <ReceiptIcon />,    color: '#2e7d32', label: 'Receipt' },
  issue:      { icon: <IssueIcon />,      color: '#1565c0', label: 'Issue' },
  transfer:   { icon: <TransferIcon />,   color: '#616161', label: 'Transfer' },
  scrap:      { icon: <ScrapIcon />,      color: '#c62828', label: 'Scrap' },
  adjustment: { icon: <AdjustmentIcon />, color: '#e65100', label: 'Adjustment' },
  return:     { icon: <ReturnIcon />,     color: '#6a1b9a', label: 'Return' },
};

function txMeta(type: string): TxMetaEntry {
  return TX_META[type] || { icon: <LotIcon />, color: '#757575', label: type };
}

// ─── Status chip ──────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'default' | 'error'> = {
  available:  'success',
  quarantine: 'warning',
  consumed:   'default',
  expired:    'error',
  rejected:   'error',
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function LotDetailCard({ lot }: LotDetailCardProps): React.ReactElement {
  const material = lot.materialDefinition;
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <LotIcon color="primary" />
          <Typography variant="h6">Lot Details</Typography>
          <Chip
            label={lot.status}
            color={STATUS_COLOR[lot.status] || 'default'}
            size="small"
            sx={{ textTransform: 'capitalize' }}
          />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">Lot Number</Typography>
            <Typography variant="body1" fontWeight={600}>{lot.lotNumber}</Typography>
          </Grid>
          {lot.batchNumber && (
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">Batch Number</Typography>
              <Typography variant="body1">{lot.batchNumber}</Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">Material</Typography>
            <Typography variant="body1">
              {material?.name || material?.itemCode || lot.materialDefinitionId}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">Quantity</Typography>
            <Typography variant="body1">{lot.quantityValue} {lot.quantityUom}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">Production Date</Typography>
            <Typography variant="body1">{formatDateOnly(lot.productionDate)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">Expiry Date</Typography>
            <Typography variant="body1">{formatDateOnly(lot.expiryDate)}</Typography>
          </Grid>
          {lot.supplierName && (
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">Supplier</Typography>
              <Typography variant="body1">{lot.supplierName}</Typography>
            </Grid>
          )}
          {lot.supplierLotNumber && (
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">Supplier Lot #</Typography>
              <Typography variant="body1">{lot.supplierLotNumber}</Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

function TransactionTimeline({ transactions }: TransactionTimelineProps): React.ReactElement {
  if (!transactions || transactions.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={1}>Transaction Timeline</Typography>
        <Typography color="text.secondary">No transactions recorded for this lot.</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" mb={2}>Transaction Timeline</Typography>
      <Stack spacing={0}>
        {transactions.map((tx, idx) => {
          const meta = txMeta(tx.type);
          return (
            <Box key={tx.id || idx}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5 }}>
                {/* Timeline dot + line */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: meta.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      flexShrink: 0,
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    {meta.icon}
                  </Box>
                  {idx < transactions.length - 1 && (
                    <Box sx={{ width: 2, flexGrow: 1, minHeight: 16, bgcolor: 'divider', mt: 0.5 }} />
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ flexGrow: 1, pb: idx < transactions.length - 1 ? 1 : 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: meta.color }}>
                      {meta.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(tx.timestamp)}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" mt={0.5}>
                    <strong>Quantity:</strong> {tx.quantity} {tx.uom}
                  </Typography>

                  {(tx.from || tx.to) && (
                    <Typography variant="body2">
                      <strong>Location:</strong>{' '}
                      {tx.from ? tx.from : '—'}{' '}
                      <span style={{ color: '#888' }}>→</span>{' '}
                      {tx.to ? tx.to : '—'}
                    </Typography>
                  )}

                  {tx.reason && (
                    <Typography variant="body2">
                      <strong>Reason:</strong> {tx.reason}
                    </Typography>
                  )}

                  {tx.performedBy && (
                    <Typography variant="body2">
                      <strong>By:</strong> {tx.performedBy}
                    </Typography>
                  )}

                  {tx.workOrder && (
                    <Tooltip title={`Status: ${tx.workOrder.status}`}>
                      <Chip
                        icon={<WorkOrderIcon />}
                        label={`WO ${tx.workOrder.number} — ${tx.workOrder.product}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ mt: 0.5 }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {idx < transactions.length - 1 && <Divider />}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

function UsedInWorkOrders({ workOrders }: UsedInWorkOrdersProps): React.ReactElement {
  if (!workOrders || workOrders.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" mb={1}>Used In Work Orders</Typography>
        <Typography color="text.secondary">This lot has not been used in any work orders.</Typography>
      </Paper>
    );
  }

  const STATUS_WO_COLOR: Record<string, 'default' | 'info' | 'primary' | 'success' | 'error' | 'warning'> = {
    planned:     'default',
    released:    'info',
    started:     'primary',
    in_progress: 'primary',
    completed:   'success',
    cancelled:   'error',
    on_hold:     'warning',
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" mb={2}>Used In Work Orders</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        {workOrders.map((wo) => (
          <Card key={wo.id} variant="outlined" sx={{ minWidth: 220, p: 0 }}>
            <CardContent sx={{ pb: '12px !important', pt: 1.5, px: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <WorkOrderIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>{wo.number}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" noWrap>{wo.product}</Typography>
              <Chip
                label={wo.status?.replace('_', ' ')}
                color={STATUS_WO_COLOR[wo.status] || 'default'}
                size="small"
                sx={{ mt: 0.75, textTransform: 'capitalize' }}
              />
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Paper>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LotGenealogy({ initialLotNumber }: LotGenealogyProps): React.ReactElement {
  const [searchInput, setSearchInput] = useState<string>(initialLotNumber ?? '');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [traceData, setTraceData] = useState<TraceData | null>(null);

  const runSearch = useCallback(async (lotNumber: string) => {
    const trimmed = lotNumber.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setTraceData(null);

    try {
      // Step 1: find the lot by lot number
      const lotsResp = await apiFetch(
        `/master-data/material-lots?lotNumber=${encodeURIComponent(trimmed)}`,
      ) as Lot[] | { data?: Lot[] };

      const lots: Lot[] = Array.isArray(lotsResp) ? lotsResp : (lotsResp as { data?: Lot[] })?.data ?? [];

      if (lots.length === 0) {
        setError(`No lot found with lot number "${trimmed}".`);
        setLoading(false);
        return;
      }

      const lotId = lots[0].id;

      // Step 2: fetch the trace
      const trace = await apiFetch(`/master-data/material-lots/${lotId}/trace`) as TraceData;
      setTraceData(trace);
    } catch (err) {
      const e = err as { message?: string };
      setError(e.message || 'Failed to fetch lot trace.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-search when initialLotNumber is provided on mount
  useEffect(() => {
    if (initialLotNumber && initialLotNumber.trim()) {
      runSearch(initialLotNumber);
    }
    // Only run on mount / when initialLotNumber changes
  }, [initialLotNumber, runSearch]);

  const handleSearch = useCallback(() => {
    runSearch(searchInput);
  }, [searchInput, runSearch]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <Box>
      {/* Search bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Lot Number"
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. LOT-2024-001"
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={loading || !searchInput.trim()}
          >
            {loading ? 'Searching…' : 'Search'}
          </Button>
        </Stack>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {traceData && (
        <>
          <LotDetailCard lot={traceData.lot} />
          <TransactionTimeline transactions={traceData.transactions} />
          <UsedInWorkOrders workOrders={traceData.usedInWorkOrders} />
        </>
      )}

      {/* Empty state before search */}
      {!traceData && !loading && !error && (
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            color: 'text.secondary',
            borderStyle: 'dashed',
          }}
        >
          <LotIcon sx={{ fontSize: 56, mb: 1, opacity: 0.3 }} />
          <Typography variant="h6" gutterBottom>Enter a lot number to begin tracing</Typography>
          <Typography variant="body2">
            The trace will show all inventory movements and work orders associated with the lot.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
